/**
 * B2B Configuration Utility
 * Single source of truth for B2B mode.
 * When B2B is true (api.config.ts), all features in hiddenFeatures and all
 * routes in HIDDEN_B2B_ROUTES are hidden across the whole app.
 */

import { B2B } from "./env";

/**
 * Check if a feature should be hidden in B2B mode
 * @param {string} feature - Feature name to check
 * @returns {boolean} - True if feature should be hidden
 */

export const IS_SIGNUP_SHOW = true;

export const isFeatureHidden = (feature) => {
  if (!B2B) return false;

  const hiddenFeatures = [
    "signup",
    "deposit",
    "withdraw",
    "bonus",
    "affiliate",
    "social-links",
    "whatsapp",
    "transactions",
    "bank-details",
    "promotions",
  ];

  return hiddenFeatures.includes(feature.toLowerCase());
};

/**
 * Routes that should be hidden (redirect to home) when B2B is true.
 * Maps hidden features to their routes.
 */
const HIDDEN_B2B_ROUTES = [
  "/signup", // signup
  "/deposit", // deposit
  "/withdraw", // withdraw
  "/bonus-list", // bonus/bonuses (UI route in this app)
  "/bonus_statement", // bonus/bonuses
  "/bonuses", // bonuses
  "/reffer-and-earn", // affiliate
  "/transactions", // deposit-history, withdraw-history
  "/promotions", // promotions
];

/**
 * Check if a route should be hidden in B2B mode (and redirect away)
 * @param {string} path - Route path (e.g. /transaction/deposit)
 * @returns {boolean} - True if route should be hidden
 */
export const isRouteHidden = (path) => {
  if (!B2B || !path) return false;
  const normalized = path.split("?")[0].toLowerCase().replace(/\/$/, "");
  return HIDDEN_B2B_ROUTES.some(
    (route) =>
      normalized === route.toLowerCase() ||
      normalized.startsWith(route.toLowerCase() + "/"),
  );
};

/**
 * Check if B2B mode is enabled
 * @returns {boolean}
 */
export const isB2BMode = () => B2B;

/*
 * Future use (currently unused):
 * Keep this helper if we later move Header/Sidebar items to a config-driven
 * structure and want centralized B2B filtering for nested menus.
 *
 * export const filterMenuItems = (menuItems) => {
 *   if (!B2B) return menuItems;
 *
 *   const shouldHideByText = (label = "", path = "") => {
 *     const l = String(label || "").toLowerCase();
 *     const p = String(path || "").toLowerCase();
 *
 *     if (l.includes("deposit") || l.includes("withdraw")) return true;
 *     if (p.includes("deposit") || p.includes("withdraw")) return true;
 *     if (l.includes("bank") || p.includes("bank")) return true;
 *     if (l.includes("transactions") || p.includes("transactions")) return true;
 *     if (l.includes("affiliate") || p.includes("affiliate")) return true;
 *     if (l.includes("bonus") || p.includes("bonus")) return true;
 *     return false;
 *   };
 *
 *   const filterOne = (item) => {
 *     if (!item || typeof item !== "object") return null;
 *     const label = item.label || "";
 *     const path = item.path ?? item.to ?? "";
 *     if (shouldHideByText(label, path)) return null;
 *
 *     const next = { ...item };
 *     if (Array.isArray(next.subItems)) {
 *       next.subItems = next.subItems.map(filterOne).filter(Boolean);
 *     }
 *     if (Array.isArray(next.links)) {
 *       next.links = next.links.filter(
 *         (l) => !shouldHideByText(l?.label, l?.path ?? l?.to ?? ""),
 *       );
 *     }
 *
 *     return next;
 *   };
 *
 *   return (Array.isArray(menuItems) ? menuItems : [])
 *     .map(filterOne)
 *     .filter(Boolean);
 * };
 */

export default {
  isFeatureHidden,
  isRouteHidden,
  isB2BMode,
};
